import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronRight, Lock, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@reflex/ui/lib/utils';
import type { SecretManagerStatus } from '@/lib/types';

/**
 * Compact, header-aligned summary of the active `SecretManager`.
 * Rendered in the `actions` slot of the Admin → Secrets page so it sits
 * inline with the page title rather than as a body card. Clicking it
 * navigates to `/admin/secrets/storage` for the full storage detail
 * (key fingerprints, retired keys, rotation runbook).
 *
 * Visibility mirrors {@link RequireSuperAdmin} (and the server-side
 * `requirePerm` policy): deployments with auth disabled see the badge
 * too. Otherwise it hides for non-super-admins so we don't trigger a
 * guaranteed 403 round-trip every page load.
 */
export function SecretManagerCard() {
  const { authEnabled, isPlatformAdmin } = useAuth();
  const orgRoutes = useOrgRoutes();
  const enabled = !authEnabled || isPlatformAdmin;

  const statusQuery = useQuery({
    queryKey: queryKeys.secretManagerStatus,
    queryFn: api.getSecretManagerStatus,
    enabled,
  });

  if (!enabled) return null;
  const status = statusQuery.data;

  return (
    <Link
      to={orgRoutes.secretsStorage}
      aria-label="Open secret storage details"
      data-testid="secret-manager-card"
      className={cn(
        'group inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/30 px-2 py-1 text-xs',
        'text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <ManagerIcon status={status} />
      <ManagerLabel status={status} loading={statusQuery.isLoading} />
      {status?.kind === 'encrypted' && status.rotation.primaryKeyId && (
        <span
          className="font-mono text-[11px] text-muted-foreground/80"
          data-testid="manager-primary-key-id"
        >
          {status.rotation.primaryKeyId.slice(0, 8)}
        </span>
      )}
      {status?.rotation.supported &&
        status.rotation.needsRotation !== undefined &&
        status.rotation.needsRotation > 0 && (
          <span className="font-medium text-status-accent-orange" data-testid="needs-rotation-pill">
            · {status.rotation.needsRotation} pending
          </span>
        )}
      <ChevronRight className="h-3 w-3 shrink-0 opacity-60 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

function ManagerIcon({ status }: { status?: SecretManagerStatus }) {
  if (!status) return <Lock className="h-3.5 w-3.5 shrink-0" />;
  if (status.kind === 'plaintext')
    return <ShieldAlert className="h-3.5 w-3.5 shrink-0 text-status-accent-orange" />;
  if (status.kind === 'encrypted')
    return <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-status-success" />;
  return <Lock className="h-3.5 w-3.5 shrink-0" />;
}

function ManagerLabel({ status, loading }: { status?: SecretManagerStatus; loading: boolean }) {
  if (loading) return <span>Storage…</span>;
  if (!status) return <span>Storage</span>;
  if (status.kind === 'plaintext')
    return <span className="font-medium text-status-accent-orange">Plaintext</span>;
  if (status.kind === 'encrypted')
    return <span className="font-medium text-foreground">Encrypted</span>;
  return <span className="font-medium capitalize">{status.kind}</span>;
}
